import { Model } from '@/models/Model';
import JsonApiResponse, {
  toSinglePageResponse,
} from '@/models/JsonApiResponse';
import { QueryClient, QueryKey, InfiniteData } from '@tanstack/react-query';
import { ListCacheStrategy } from './BaseListCacheStrategy';

export class InfiniteCacheStrategy<
  T extends Model,
> extends ListCacheStrategy<T> {
  constructor(queryClient: QueryClient, queryKey: QueryKey) {
    super(queryClient, queryKey);
  }

  set(data: T[]): void {
    const listParents = this.getRawData<InfiniteData<JsonApiResponse<T>>>();
    if (!listParents) return;

    const lastPageIndex = listParents.pages.length - 1;
    let cursor = 0;

    const pages = listParents.pages.map((page, index) => {
      const size =
        index === lastPageIndex ? data.length - cursor : page.results.length;
      const results = data.slice(cursor, cursor + size);
      cursor += results.length;

      return { ...page, results };
    });

    this.queryClient.setQueryData<InfiniteData<JsonApiResponse<T>>>(
      this.queryKey,
      { ...listParents, pages },
    );
  }

  setIfAbsent(data: T[]): void {
    this.queryClient.setQueryData<InfiniteData<JsonApiResponse<T>>>(
      this.queryKey,
      old =>
        old ?? { pages: [toSinglePageResponse(data)], pageParams: [undefined] },
    );
  }

  get(): T[] | undefined {
    const listParents = this.getRawData<InfiniteData<JsonApiResponse<T>>>();

    return listParents?.pages.flatMap(page => page.results);
  }

  getById = (id: number) => this.get()?.find(item => item.id === id);
}
